import React, { useMemo, useState, useRef, useEffect } from 'react';
import { downDataTip, type TDownloadTipType } from '@/pages/aigc/components/DownloadTips';
import { useIntl } from 'umi';

/** 自定义通知文案配置 */
export interface IDownloadNotificationConfig {
  loadingText?: React.ReactNode;
  successText?: React.ReactNode;
  errorText?: React.ReactNode;
}

/**
 * 下载通知 Hook
 * 参考经分 useNotificationOfDownloadTable：仅 status 变化时弹出，切换语言不重建
 */
export const useDownloadNotification = (
  tableName?: string,
  enabled = true,
  config?: IDownloadNotificationConfig,
) => {
  const { formatMessage } = useIntl();
  const notificationRef = useRef<{ destroy: () => void; }>({ destroy: () => {} });
  const [status, setStatus] = useState<TDownloadTipType | ''>('');

  const text = useMemo(() => {
    const textFun = (textKey: string) => (
      <>
        {tableName && (
          <span style={{ fontWeight: 600 }}>{tableName}&nbsp;</span>
        )}
        {formatMessage({ id: textKey })}
      </>
    );
    return {
      loadingText: config?.loadingText ?? textFun('common_down_loading_text_loading'),
      successText: config?.successText ?? textFun('common_down_success_text'),
      errorText: config?.errorText ?? textFun('common_down_error_text'),
    };
  }, [tableName, formatMessage, config?.loadingText, config?.successText, config?.errorText]);

  useEffect(() => {
    if (!enabled || !status) {
      notificationRef.current.destroy();
    } else {
      notificationRef.current.destroy();
      notificationRef.current = downDataTip(
        status,
        text[`${status}Text` as keyof typeof text],
      );
    }
    return () => {
      notificationRef.current.destroy();
    };
  }, [status]);

  return { status, setNoticeStatus: setStatus };
};
